/**
 * Opis zadania z formatowaniem.
 *
 * Pole działa tak, jak wygląda: kliknięcie **B** pogrubia zaznaczony tekst od
 * razu, a nie wstawia gwiazdki, którą trzeba sobie wyobrazić. Powód jest
 * prozaiczny — opis pisze się raz, a czyta dziesięć razy, i to czytelność
 * decyduje o tym, czy ktoś ten opis w ogóle uzupełni.
 *
 * Pod spodem leży tekst (`src/lib/richtext.ts`), nie HTML. Dzięki temu:
 *
 * * zapisany opis czyta się także tam, gdzie nikt go nie renderuje (mail),
 * * pole `description` w `/api/v1` zostaje tekstem i nie łamie kontraktu,
 * * treść nigdy nie wraca na stronę jako HTML — widok składa ją z rozpoznanych
 *   bloków, więc nie ma czego odkażać.
 *
 * Zawartość pola ustawiamy **tylko przy montowaniu**. Panel zadania montuje
 * się z kluczem zadania, więc każde zadanie dostaje własne pole, a kursor nie
 * skacze na koniec przy każdym naciśnięciu klawisza.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { fromHtml, isEmpty, parse, toHtml, type Span } from "../lib/richtext";

type Command = {
  key: string;
  label: string;
  hint: string;
  glyph: ReactNode;
};

const MARKS: Command[] = [
  { key: "bold", label: "Pogrubienie", hint: "Ctrl+B", glyph: <b>B</b> },
  { key: "italic", label: "Kursywa", hint: "Ctrl+I", glyph: <i>I</i> },
  { key: "underline", label: "Podkreślenie", hint: "Ctrl+U", glyph: <u>U</u> },
  { key: "strikeThrough", label: "Przekreślenie", hint: "", glyph: <s>S</s> },
];

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const emitted = useRef(value);
  const [active, setActive] = useState<Record<string, boolean>>({});
  const [empty, setEmpty] = useState(isEmpty(value));

  const readState = useCallback(() => {
    const element = box.current;
    if (!element || !element.contains(document.getSelection()?.anchorNode ?? null)) return;

    const state: Record<string, boolean> = {};
    for (const { key } of MARKS) {
      try {
        state[key] = document.queryCommandState(key);
      } catch {
        state[key] = false;
      }
    }
    state.list = blockTag(element) === "LI";
    state.h1 = blockTag(element) === "H1";
    state.h2 = blockTag(element) === "H2";
    setActive(state);
  }, []);

  useEffect(() => {
    const element = box.current;
    if (!element) return;
    element.innerHTML = toHtml(value);
    emitted.current = value;
    // Bez tego Chrome formatuje stylami w `span`, a nie znacznikami — czyli
    // zapisem, którego serializator nie ma jak odczytać.
    try {
      document.execCommand("styleWithCSS", false, "false");
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {
      /* starsze przeglądarki: formatowanie działa, tylko brzydziej */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", readState);
    return () => document.removeEventListener("selectionchange", readState);
  }, [readState]);

  const emit = useCallback(() => {
    const element = box.current;
    if (!element) return;
    const next = fromHtml(element);
    emitted.current = next;
    setEmpty(isEmpty(next));
    onChange(next);
  }, [onChange]);

  const run = (command: string, argument?: string) => {
    box.current?.focus();
    try {
      document.execCommand(command, false, argument);
    } catch {
      return;
    }
    emit();
    readState();
  };

  const heading = (tag: "H1" | "H2") =>
    run("formatBlock", blockTag(box.current) === tag ? "<p>" : `<${tag.toLowerCase()}>`);

  return (
    <div className="editor">
      <div className="editor__bar" role="toolbar" aria-label={`Formatowanie: ${label}`}>
        {MARKS.map((command) => (
          <button
            key={command.key}
            type="button"
            title={command.hint ? `${command.label} (${command.hint})` : command.label}
            aria-label={command.label}
            aria-pressed={Boolean(active[command.key])}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => run(command.key)}
          >
            {command.glyph}
          </button>
        ))}
        <span className="editor__sep" aria-hidden="true" />
        <button
          type="button"
          title="Nagłówek"
          aria-label="Nagłówek"
          aria-pressed={Boolean(active.h1)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => heading("H1")}
        >
          H1
        </button>
        <button
          type="button"
          title="Podtytuł"
          aria-label="Podtytuł"
          aria-pressed={Boolean(active.h2)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => heading("H2")}
        >
          H2
        </button>
        <button
          type="button"
          title="Lista wypunktowana"
          aria-label="Lista wypunktowana"
          aria-pressed={Boolean(active.list)}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => run("insertUnorderedList")}
        >
          <span aria-hidden="true">•—</span>
        </button>
      </div>

      <div className="editor__field">
        <div
          ref={box}
          className="richtext"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          onInput={emit}
          onBlur={emit}
          onKeyUp={readState}
          onMouseUp={readState}
          onPaste={(event) => {
            // Wklejamy sam tekst. HTML skopiowany ze strony wnosi obce style
            // i znaczniki, których i tak nie zapiszemy — lepiej odciąć je od
            // razu, niż zgubić po zapisie.
            event.preventDefault();
            const text = event.clipboardData.getData("text/plain");
            document.execCommand("insertText", false, text);
          }}
        />
        {empty && placeholder && (
          <p className="editor__placeholder" aria-hidden="true">
            {placeholder}
          </p>
        )}
      </div>
    </div>
  );
}

/** Widok opisu bez możliwości edycji — dla roli tylko do odczytu. */
export function RichTextView({ value }: { value: string | null | undefined }) {
  const blocks = parse(value);
  if (!blocks.length) return <p className="dim">Brak opisu.</p>;

  return (
    <div className="richtext">
      {blocks.map((block, index) => {
        if (block.kind === "ul") {
          return (
            <ul key={index}>
              {block.items.map((item, position) => (
                <li key={position}>{spans(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.kind === "h1") return <h1 key={index}>{spans(block.spans)}</h1>;
        if (block.kind === "h2") return <h2 key={index}>{spans(block.spans)}</h2>;
        return <p key={index}>{spans(block.spans)}</p>;
      })}
    </div>
  );
}

function spans(items: Span[]): ReactNode[] {
  return items.map((span, index) => {
    let node: ReactNode = span.text;
    if (span.marks.includes("italic")) node = <em>{node}</em>;
    if (span.marks.includes("strike")) node = <s>{node}</s>;
    if (span.marks.includes("underline")) node = <u>{node}</u>;
    if (span.marks.includes("bold")) node = <strong>{node}</strong>;
    return <span key={index}>{node}</span>;
  });
}

/** Znacznik bloku, w którym stoi kursor — `queryCommandState` tego nie mówi. */
function blockTag(root: HTMLElement | null): string {
  const anchor = document.getSelection()?.anchorNode ?? null;
  if (!root || !anchor || !root.contains(anchor)) return "";

  let node: Node | null = anchor.nodeType === Node.ELEMENT_NODE ? anchor : anchor.parentNode;
  while (node && node !== root) {
    if (node instanceof HTMLElement && ["H1", "H2", "LI", "P", "DIV"].includes(node.tagName)) {
      return node.tagName;
    }
    node = node.parentNode;
  }
  return "";
}
